import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, MagnifyingGlass } from "@phosphor-icons/react";
import { BUSINESS } from "@/config";

const HERO_IMG =
  "https://images.unsplash.com/photo-1564890379305-8621a5a20aca?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920";

const lines = ["GRIP THE", "ROAD LIKE", "YOU MEAN IT."];

const lineVariant = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 0.9, delay: 0.35 + i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.18]);

  const scrollTo = (id) => document.querySelector(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section id="top" ref={ref} className="relative min-h-[100svh] flex items-end overflow-hidden" data-testid="hero">
      <motion.div style={{ y: imgY, scale: imgScale }} className="absolute inset-0">
        <img src={HERO_IMG} alt="Performance auto shop" className="w-full h-full object-cover" loading="eager" />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/60 to-background/30" />
        <div className="absolute inset-0 bg-gradient-to-r from-background/70 to-transparent" />
      </motion.div>

      <div className="relative z-10 max-w-7xl mx-auto w-full px-5 sm:px-8 pb-16 sm:pb-24">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="flex items-center gap-3 mb-6"
        >
          <span className="h-px w-10 bg-primary" />
          <span className="text-xs font-bold tracking-[0.3em] uppercase text-primary">{BUSINESS.tagline}</span>
        </motion.div>

        <h1 className="font-hero uppercase leading-[0.88] tracking-normal text-6xl sm:text-8xl lg:text-9xl">
          {lines.map((line, i) => (
            <span key={i} className="block overflow-hidden">
              <motion.span custom={i} variants={lineVariant} initial="hidden" animate="show" className="block">
                {line.split("YOU MEAN").length > 1 ? (
                  <>
                    YOU <span className="text-primary">MEAN</span> IT.
                  </>
                ) : (
                  line
                )}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 0.7 }}
          className="mt-8 flex flex-col sm:flex-row sm:items-center gap-5 max-w-2xl"
        >
          <p className="text-base sm:text-lg text-foreground/80 leading-relaxed max-w-md">
            Search by size or brand, check live inventory, and roll out the same day. Retail and wholesale,
            done right.
          </p>
          <button
            onClick={() => scrollTo("#search")}
            className="shrink-0 inline-flex items-center gap-2 bg-primary text-white font-bold px-7 py-4 rounded-sm hover:bg-red-500 transition-colors uppercase tracking-wide"
            data-testid="hero-search-cta"
          >
            <MagnifyingGlass size={20} weight="bold" /> Find My Tires
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.4 }}
          className="mt-12 grid grid-cols-2 sm:grid-cols-4 gap-px bg-white/10 border border-white/10 max-w-3xl"
        >
          {BUSINESS.stats.map((s) => (
            <div key={s.label} className="bg-background/80 backdrop-blur px-5 py-4">
              <div className="font-display font-black text-2xl sm:text-3xl text-primary">{s.value}</div>
              <div className="text-xs text-muted-foreground tracking-wide mt-1">{s.label}</div>
            </div>
          ))}
        </motion.div>
      </div>

      <motion.button
        onClick={() => scrollTo("#search")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1, y: [0, 8, 0] }}
        transition={{ opacity: { delay: 1.6 }, y: { repeat: Infinity, duration: 1.8 } }}
        className="absolute bottom-6 right-6 z-10 hidden sm:flex text-muted-foreground hover:text-primary"
        aria-label="Scroll down"
      >
        <ArrowDown size={28} />
      </motion.button>
    </section>
  );
};
